import { ReactNode, useState } from "react";
import { Pressable } from "react-native";

import { BottomSheet } from "@/components/ui/bottom-sheet";
import Bugsnag from "@/utils/bugsnag";

interface Props {
  children?: ReactNode;
  label: string;
  lat?: null | number;
  lng?: null | number;
  placeId?: null | string;
}

export default function OpenDirections({
  children,
  label,
  lat,
  lng,
  placeId,
}: Props) {
  const [open, setOpen] = useState(false);

  const hasCoords = lat != null && lng != null;
  const encoded = encodeURIComponent(label);

  function getGoogleMapsURL() {
    if (placeId) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encoded}&destination_place_id=${placeId}`;
    }
    if (hasCoords) {
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return null;
  }

  function openUrl(url: string) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function handlePress() {
    const googleURL = getGoogleMapsURL();

    if (!hasCoords && !googleURL) {
      Bugsnag.notify(new Error("missing address data for directions"));
      window.alert("there was an error loading the address");
      return;
    }

    if (isSafari() && hasCoords) {
      setOpen(true);
    } else if (googleURL) {
      openUrl(googleURL);
    }
  }

  function openApple() {
    if (hasCoords) {
      openUrl(`https://maps.apple.com/?daddr=${encoded}@${lat},${lng}`);
    }
    setOpen(false);
  }

  function openGoogle() {
    const url = getGoogleMapsURL();
    if (url) openUrl(url);
    setOpen(false);
  }

  const options = hasCoords
    ? [
        { label: "open in apple maps", onPress: openApple },
        { label: "open in google maps", onPress: openGoogle },
      ]
    : [{ label: "open in google maps", onPress: openGoogle }];

  return (
    <>
      <Pressable onPress={handlePress}>{children}</Pressable>

      <BottomSheet
        onClose={() => setOpen(false)}
        options={options}
        visible={open}
      />
    </>
  );
}

function isSafari(): boolean {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS|Chromium/.test(ua);
}
